import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Stack, TextField, Button, Alert, CircularProgress,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { AccountBalance, QrCode2 } from '@mui/icons-material';
import { addBankAccount, addPayoutUPI } from '../../store/slices/walletSlice';

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const IFSC_ERROR_MSG = 'Invalid IFSC format. Expected: 4 letters, then 0, then 6 alphanumeric characters (e.g., SBIN0001234).';

const UPI_VPA_REGEX = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
const UPI_ERROR_MSG = 'Invalid UPI ID format. Expected: name@provider (e.g., name@okaxis).';

const BankAccountForm = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((s) => s.wallet);
  const [methodType, setMethodType] = useState('bank');
  const [form, setForm] = useState({
    bank_name: '',
    account_number: '',
    ifsc: '',
    account_holder_name: '',
  });
  const [upiId, setUpiId] = useState('');
  const [localError, setLocalError] = useState('');

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleToggle = (_, val) => {
    if (val !== null) {
      setMethodType(val);
      setLocalError('');
      if (val === 'upi') {
        setForm({ bank_name: '', account_number: '', ifsc: '', account_holder_name: '' });
      } else {
        setUpiId('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!form.bank_name || !form.account_number || !form.ifsc || !form.account_holder_name) {
      setLocalError('All fields are required');
      return;
    }
    if (!IFSC_REGEX.test(form.ifsc)) {
      setLocalError(IFSC_ERROR_MSG);
      return;
    }
    try {
      await dispatch(addBankAccount(form)).unwrap();
      setForm({ bank_name: '', account_number: '', ifsc: '', account_holder_name: '' });
      if (onSuccess) onSuccess();
    } catch {
      // error in Redux
    }
  };

  const handleUpiSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!upiId.trim()) {
      setLocalError('UPI ID is required');
      return;
    }
    if (!UPI_VPA_REGEX.test(upiId.trim())) {
      setLocalError(UPI_ERROR_MSG);
      return;
    }
    try {
      await dispatch(addPayoutUPI({ upi_id: upiId.trim() })).unwrap();
      setUpiId('');
      if (onSuccess) onSuccess();
    } catch {
      // error handled by Redux (error.payoutUPI)
    }
  };

  const displayBankError = localError || error.bankAccounts;
  const displayUpiError = localError || error.payoutUPI;

  return (
    <Box>
      <ToggleButtonGroup
        value={methodType}
        exclusive
        onChange={handleToggle}
        aria-label="Payout method type"
        color="primary"
        fullWidth
        sx={{ mb: 2.5 }}
      >
        <ToggleButton value="bank" sx={{ py: 1, textTransform: 'none', gap: 0.75 }}>
          <AccountBalance fontSize="small" />
          Bank Account
        </ToggleButton>
        <ToggleButton value="upi" sx={{ py: 1, textTransform: 'none', gap: 0.75 }}>
          <QrCode2 fontSize="small" />
          UPI ID
        </ToggleButton>
      </ToggleButtonGroup>

      {methodType === 'bank' && (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Bank Name"
              value={form.bank_name}
              onChange={handle('bank_name')}
              size="small"
              fullWidth
              required
            />
            <TextField
              label="Account Holder Name"
              value={form.account_holder_name}
              onChange={handle('account_holder_name')}
              size="small"
              fullWidth
              required
            />
            <TextField
              label="Account Number"
              value={form.account_number}
              onChange={handle('account_number')}
              size="small"
              fullWidth
              required
            />
            <TextField
              label="IFSC Code"
              value={form.ifsc}
              onChange={(e) => {
                setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }));
                setLocalError('');
              }}
              size="small"
              fullWidth
              required
              placeholder="e.g., SBIN0001234"
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />

            {displayBankError && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                {displayBankError}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={loading.bankAccounts}
              startIcon={loading.bankAccounts ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 3 }}
            >
              {loading.bankAccounts ? 'Registering...' : 'Add Account'}
            </Button>
          </Stack>
        </Box>
      )}

      {methodType === 'upi' && (
        <Box component="form" onSubmit={handleUpiSubmit}>
          <Stack spacing={2}>
            <TextField
              label="UPI ID (VPA)"
              value={upiId}
              onChange={(e) => { setUpiId(e.target.value); setLocalError(''); }}
              size="small"
              fullWidth
              required
              placeholder="e.g., name@okaxis"
              inputProps={{ 'aria-label': 'UPI Virtual Payment Address' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Use the UPI ID from your payment app (GPay, PhonePe, Paytm, your bank's app, etc.) — check its Profile / UPI settings for the exact address.
            </Typography>

            {displayUpiError && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                {displayUpiError}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={loading.payoutUPI}
              startIcon={loading.payoutUPI ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 3 }}
            >
              {loading.payoutUPI ? 'Verifying...' : 'Add UPI'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default BankAccountForm;
